import MapSearch from '../search-map';
import DrawYourSearch from '../components/draw-your-search';
import SearchThisArea from '../components/search-this-area';
import { hasUserClickedProperty } from '../storage';
import { getPropertyMarkerType } from '../property/marker';
import { Coordinate } from './coordinate.js';
import { State, getState } from '../../routing/history';

class Property {
    mapSearch: MapSearch;
    id: number;
    longitude: number;
    latitude: number;
    precision: string;
    index: number;
    url: URL;
    _visited = false;
    _active = false;
    _hover = false;
    _saved = false;
    _contacted = false;
    _discarded = false;
    _markerType: string = '';

    constructor( property: any, mapSearch: MapSearch ) {
        this.mapSearch = mapSearch;
        this.id = property.id;
        this.longitude = property.lng;
        this.latitude = property.lat;
        this.precision = property.precision || 'approx';
        this.index = property.index;
        this.url = new URL( this.mapSearch.propertySearchURL.toString() + '/' + this.id );
        this._visited = hasUserClickedProperty( this.id );
        this._updateMarkerType();
    }

    /**
     * @returns HTML String of the property
     */
    async getHTML(): Promise<string | Error> {
        const url = this.url;

        if ( this.mapSearch.previousSearchState ) {
            const previouSearch = new URLSearchParams( this.mapSearch.previousSearchState );
            url.searchParams.set( 'order', previouSearch.get( 'order' ) || '' );

            const area = previouSearch.get( 'area' );
            if ( area && area.length > 0 ) {
                url.searchParams.set( 'viewMode', DrawYourSearch.id );
            }
        }

        if ( this.mapSearch.activeComponents.has( SearchThisArea.id ) ) {
            url.searchParams.set( 'viewMode', SearchThisArea.id );
        }

        const requestURL = new URL( url.toString() );

        if ( this.mapSearch.currentLocation ) {
            requestURL.searchParams.set( 'searchLocation', this.mapSearch.currentLocation.id );
        }

        const currentState: State = getState();

        if ( currentState && currentState.meta && currentState.meta.propertySearchParams && currentState.meta.propertySearchParams.radius ) {
            requestURL.searchParams.set( 'searchRadius', currentState.meta.propertySearchParams.radius );
        }

        const response: Response = await window.http( requestURL );

        if ( response.status !== 200 ) {
            return new Error( 'failed to get property' );
        }

        const html: string = await response.text();

        return html;
    }

    /**
     * @returns property as Geo JSON Feature Object
     */
    asFeature(): any {
        return {
            id: this.id,
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [
                    this.longitude,
                    this.latitude,
                ],
            },
            properties: {
                icon: this.getIcon(),
                id: this.id,
                type: 'property',
                index: this.index,
            },
        };
    }

    /**
     * @returns Object containing property state
     */
    getIcon(): string {
        return this._markerType;
    }

    /**
     * @returns Boolean - whether the property lat/lon is the exact location of the property
     */
    isExact(): boolean {
        return this.precision === 'exact';
    }

    /**
     * @returns Object - contains the lat/lon pair of the property location
     */
    position(): Coordinate {
        return {
            lng: this.longitude,
            lat: this.latitude,
        };
    }

    destroy(): void {
        return;
    }

    _updateMarkerType(): void {
        this._markerType = getPropertyMarkerType( {
            precision: this.precision,
            contacted: this.contacted,
            saved: this.saved,
            hover: this.hover,
            visited: this.visited,
            active: this.active,
            discarded: this.discarded,
        } );
    }

    get visited(): boolean {
        return this._visited;
    }

    set visited( value: boolean ) {
        this._visited = value;
        this._updateMarkerType();
    }

    get active(): boolean {
        return this._active;
    }

    set active( value: boolean ) {
        this._active = value;
        this._updateMarkerType();
    }

    get hover(): boolean {
        return this._hover;
    }

    set hover( value: boolean ) {
        this._hover = value;
        this._updateMarkerType();
    }

    get saved(): boolean {
        return this._saved;
    }

    set saved( value: boolean ) {
        this._saved = value;
        this._updateMarkerType();
    }

    get contacted(): boolean {
        return this._contacted;
    }

    set contacted( value: boolean ) {
        this._contacted = value;
        this._updateMarkerType();
    }

    get discarded(): boolean {
        return this._discarded;
    }

    set discarded( value: boolean ) {
        this._discarded = value;
        this._updateMarkerType();
    }
}

export default Property;
